import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { X, CalendarDays } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import { PrntrCard } from './ui/prntr-input'
import { DateRow, NumRow, TextareaField } from './ui/prntr-fields'
import { useMaintenanceCreate, useMaintenanceUpdate } from '../lib/hooks'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'

// A single maintenance window, as returned by useMaintenanceGetByAircraft.
export type MaintenanceRecordItem = {
  id: string
  startDate: string | Date
  durationDays: number
  endDate: string | Date
  note: string | null
}

interface AircraftMaintenanceDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
  // When set, the drawer edits this window; otherwise it creates a new one.
  record?: MaintenanceRecordItem | null
}

const NOTE_MAX_LENGTH = 500

const MaintenanceFormSchema = z.object({
  startDate: z.string().min(1, 'Start date is required'),
  durationDays: z
    .number({ invalid_type_error: 'Enter a number of days' })
    .int()
    .min(1, 'At least 1 day'),
  note: z.string().max(NOTE_MAX_LENGTH, 'Note is too long').optional(),
})

type MaintenanceFormData = z.infer<typeof MaintenanceFormSchema>

export function AircraftMaintenanceDrawer({
  aircraftId,
  open,
  onClose,
  record,
}: AircraftMaintenanceDrawerProps) {
  const { isAdmin } = useIsAdmin()
  const { mutate: createMaintenance, isPending: isCreating } =
    useMaintenanceCreate(aircraftId)
  const { mutate: updateMaintenance, isPending: isUpdating } =
    useMaintenanceUpdate(aircraftId)

  const editingId = record?.id ?? null
  const isSaving = isCreating || isUpdating

  const today = dayjs().format('YYYY-MM-DD')

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<MaintenanceFormData>({
    resolver: zodResolver(MaintenanceFormSchema),
    defaultValues: { startDate: today, durationDays: 1, note: '' },
    mode: 'onChange',
  })

  // Sync the form to the target record (edit) or fresh defaults (create) when
  // the drawer opens.
  useEffect(() => {
    if (!open) return
    if (record) {
      reset({
        startDate: dayjs(record.startDate).format('YYYY-MM-DD'),
        durationDays: record.durationDays,
        note: record.note ?? '',
      })
    } else {
      reset({ startDate: today, durationDays: 1, note: '' })
    }
    // Validate the freshly-reset values so `isValid` reflects the prefilled
    // (and already-submittable) create defaults without waiting for an edit.
    void trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record])

  const noteLength = watch('note')?.length ?? 0

  const onSubmit = handleSubmit((data) => {
    const note = data.note?.trim() ? data.note.trim() : null

    if (editingId) {
      updateMaintenance(
        {
          id: editingId,
          startDate: new Date(data.startDate),
          durationDays: data.durationDays,
          note,
        },
        {
          onSuccess: () => {
            toast.success('Maintenance updated')
            onClose()
          },
          onError: () => toast.error('Failed to update maintenance'),
        }
      )
      return
    }

    createMaintenance(
      {
        aircraftId,
        startDate: new Date(data.startDate),
        durationDays: data.durationDays,
        note,
      },
      {
        onSuccess: () => {
          toast.success('Maintenance scheduled')
          onClose()
        },
        onError: () => toast.error('Failed to schedule maintenance'),
      }
    )
  })

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {editingId ? 'Edit Maintenance' : 'Schedule Maintenance'}
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-6"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <form
            id="maintenance-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-4"
          >
            <PrntrCard>
              <DateRow
                control={control}
                name="startDate"
                label={
                  <span className="flex items-center gap-2">
                    <CalendarDays className="size-4" /> Start Date
                  </span>
                }
                minDate={editingId ? undefined : dayjs(today).toDate()}
                readOnly={!isAdmin}
                error={errors.startDate?.message}
              />
              <NumRow
                control={control}
                name="durationDays"
                label="Number of Days"
                integer
                readOnly={!isAdmin}
                error={errors.durationDays?.message}
              />
            </PrntrCard>
            <div className="flex flex-col gap-1">
              <TextareaField
                control={control}
                name="note"
                label="Note"
                placeholder="e.g. Scheduled engine inspection"
                readOnly={!isAdmin}
                error={errors.note?.message}
              />
              <p className="text-xs text-content-primary-subtle">
                {noteLength}/{NOTE_MAX_LENGTH} characters
              </p>
            </div>
          </form>
        </div>

        <div className="shrink-0 border-t border-border-L2 px-4 pt-3 pb-4">
          {isAdmin ? (
            <div className="flex flex-row gap-2">
              <Button
                type="button"
                variant="secondary"
                size="mini"
                className="flex-1"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="maintenance-form"
                variant="primary"
                size="mini"
                className="flex-1"
                disabled={isSaving || !isValid || (!!editingId && !isDirty)}
                loading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
